export const SPEECH_RECOGNITION_ERROR_EVENT = 'portal-speech-recognition-error';

type SpeechRecognitionErrorCode =
  | 'aborted'
  | 'audio-capture'
  | 'bad-grammar'
  | 'language-not-supported'
  | 'network'
  | 'no-speech'
  | 'not-allowed'
  | 'service-not-allowed';

type SpeechRecognitionErrorDetail = {
  error: SpeechRecognitionErrorCode | string;
};

type PatchedPrototype = SpeechRecognition & {
  __portalStartPatched?: boolean;
  __portalOnErrorWrapped?: boolean;
};

function getNativeSpeechRecognitionConstructor():
  | (new () => SpeechRecognition)
  | undefined {
  if (typeof window === 'undefined') return undefined;

  return (
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    undefined
  );
}

function dispatchSpeechRecognitionError(error: string) {
  window.dispatchEvent(
    new CustomEvent<SpeechRecognitionErrorDetail>(
      SPEECH_RECOGNITION_ERROR_EVENT,
      { detail: { error } }
    )
  );
}

function wrapInstanceOnError(
  instance: SpeechRecognition,
  nativeStart: SpeechRecognition['start']
) {
  const marked = instance as PatchedPrototype;
  if (marked.__portalOnErrorWrapped) return;

  marked.__portalOnErrorWrapped = true;
  const existingHandler = instance.onerror;

  instance.onerror = (event: SpeechRecognitionErrorEvent) => {
    const code = event?.error;
    if (code) {
      dispatchSpeechRecognitionError(code);

      if (code === 'network' && instance.continuous) {
        instance.continuous = false;
        try {
          nativeStart.call(instance);
        } catch {
          // Surfaced via toast in the UI layer.
        }
      }
    }

    existingHandler?.call(instance, event);
  };
}

/**
 * Patches the native speech recognition prototype so PromptInput's built-in mic
 * works even when the component library captured the constructor before this runs.
 */
export function ensureSpeechRecognitionBootstrap(): void {
  const NativeCtor = getNativeSpeechRecognitionConstructor();
  if (!NativeCtor) return;

  const proto = NativeCtor.prototype as PatchedPrototype;
  if (proto.__portalStartPatched) return;

  const nativeStart = proto.start;
  proto.start = function startWithLocaleAndErrorForwarding(
    this: SpeechRecognition
  ) {
    if (!this.lang) {
      this.lang = navigator.language || 'en-GB';
    }

    wrapInstanceOnError(this, nativeStart);

    try {
      return nativeStart.call(this);
    } catch (error) {
      if (error instanceof DOMException && this.continuous) {
        this.continuous = false;
        return nativeStart.call(this);
      }

      if (error instanceof DOMException) {
        dispatchSpeechRecognitionError(error.name);
      }

      throw error;
    }
  };

  proto.__portalStartPatched = true;

  if (!window.SpeechRecognition && window.webkitSpeechRecognition) {
    window.SpeechRecognition = window.webkitSpeechRecognition;
  }
}

export async function preflightMicrophoneAccess(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

export function speechRecognitionErrorMessage(
  error: SpeechRecognitionErrorDetail['error']
): string {
  switch (error) {
    case 'not-allowed':
      return 'Microphone access is blocked for this site. Allow it in your browser settings and try again.';
    case 'service-not-allowed':
      return 'Speech recognition is blocked by your browser or device policy.';
    case 'network':
      return 'Voice input could not reach the speech service. Check VPN or firewall settings, or type your prompt instead.';
    case 'audio-capture':
      return 'No microphone was detected. Check your audio input device and try again.';
    case 'no-speech':
      return 'No speech was detected. Try speaking again.';
    case 'language-not-supported':
      return 'Speech recognition is not available for your browser language.';
    case 'aborted':
      return 'Speech recognition was interrupted. Try again.';
    default:
      return 'Voice input is unavailable right now. Type your prompt instead.';
  }
}
